import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, groupBy, map, values } from 'lodash';
import moment from 'moment/min/moment-with-locales';
import { FormattedNumber } from 'react-intl';
import { toggleModal } from '../app-main/actions/modal';
import Table from '../elements-table/table';
import VariablePricingForm from './forms/VariablePricingForm';
import { updateShowVariablePriceForm } from '../app-main/actions/calendarPricing';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import IntlProviderHOC from '../app-main/containers/IntlProviderHOC';
import { Button } from '@ps-design-system/components/Button';
import TrashIcon from '@ps-design-system/icons/Trash/Trash20';
import { Text } from '@ps-design-system/components/Text';

const CalendarRate = ({
  listing, isFandB,
  calendarPrice,
  dispatch,
  showVariablePricingForm,
  intl
}) => {
  // Receives a group of information for 1 calendar rate (group contains each day in this rate)
  // Group: [{ group_id: 1, day: 1 }, { group_id: 1, day: 2 }]
  // Returns a localized string for available days (e.g. "Mon, Wed, Fri")
  const getDayRangeString = (group = []) => {
    const dows = map(group, (info) => {
      return get(info, 'dow', 0);
    });
    return dows.map(dow => moment().isoWeekday(dow).locale(intl.locale).format('ddd')).join(', ');
  };

  // Receives a group of information for 1 calendar rate (group contains each day in this rate)
  // Group: [{ group_id: 1, day: 1 }, { group_id: 1, day: 2 }]
  // Gets information from first element (at least 1 day present)
  // Returns a localized string for available hours (e.g. "11:00 AM - 4:00 PM")
  const getTimeRangeString = (group = []) => {
    const info = get(group, '[0]');
    const isAllDay = get(info, 'all_day', false);
    if (isAllDay) {
      return intl.formatMessage({ id: 'hosting.calendar_rate.all_day', defaultMessage: 'All day' });
    }
    const start = get(info, 'start.time', {});
    const end = get(info, 'end.time', {});

    const toLocalizedString = (time) => {
      return intl.formatTime(moment(`${time.h}:${time.m}`, 'HH:mm').format());
    };

    return `${toLocalizedString(start)} - ${toLocalizedString(end)}`;
  };

  const prices = get(calendarPrice, 'prices', []);
  const groupedPrices = values(groupBy(prices, 'group_id'));
  const currency = get(listing, 'prices.0.currency', '');
  const handleAddVariableClick = () => dispatch(updateShowVariablePriceForm(true));
  const handleCancel = () => dispatch(updateShowVariablePriceForm(false));

  return (
    <div>
      <Text element="h4" typeface="headline-3-m" className="tw-m-0 tw-mb-8">
        <FormattedMessage id="hosting.calendar_rate.day_of_week_heading" defaultMessage="Day of week pricing"/>
      </Text>
      <Text element="p" className="tw-m-0 tw-leading-28 tw-text-gray-600">
        {
          isFandB ?
            <FormattedMessage id="hosting.calendar_rate.food_and_bar_rate_tax_tip" defaultMessage="Add rates that apply to day of week and time of day. Such as a weekend or weeknight rate. When applicable, this variable rate will become the hourly food and bar minumum. Tax and tip will remain the same."/>
            : <FormattedMessage id="hosting.calendar_rate.food_and_bar_rate" defaultMessage="Add rates that apply to day of week and time of day. Such as a weekend or weeknight rate."/>
        }
      </Text>
      <div className="clearfix" />
      {
        groupedPrices.length ?
          <div className="row margin-lg-top margin-md-bottom">
            <div className="col-xs-12">
              <Table
                tableHeaders={
                  [
                    { label: <Text typeface="headline-1-m"><FormattedMessage id="hosting.calendar_rate.description_table_label" defaultMessage="Description"/></Text>, classname: 'flex-basis-50' },
                    { label: <Text typeface="headline-1-m"><FormattedMessage id="hosting.calendar_rate.hourly_price_table_label" defaultMessage="Hourly Price"/></Text>, classname: 'flex-basis-35 text-right' },
                    { label: '', classname: 'flex-basis-15 text-right' }
                  ]
                }
                tableBodyClass="calendar-price-table-body"
                tableBody={
                  <div>
                    {
                      groupedPrices.map((group, i) => {
                        const { name, group_id, hourly_rate } = get(group, '[0]');
                        const panelBody = classnames('flex middle calendar-price-panel-body',
                          { 'border-bottom': groupedPrices.length - 1 !== i });
                        const openDeletePricingModal = () => (
                          dispatch(toggleModal({
                            modalId: 'DeletePricingModal',
                            modalData: {
                              priceId: group_id
                            }
                          }))
                        );

                        return (
                          <div key={group_id} className={panelBody}>
                            <div className="flex-basis-50">
                              <div>
                                <Text element="div" typeface="body-2-m" className="ellipsis">{name}</Text>
                                <Text element="div" typeface="body-2" className="tw-text-gray-600">
                                  <div>
                                    {getDayRangeString(group)}
                                  </div>
                                  <div>
                                    {getTimeRangeString(group)}
                                  </div>
                                </Text>
                              </div>
                            </div>
                            <IntlProviderHOC>
                              <Text element="div" typeface="body-2" className="flex-basis-35 text-right">
                                <FormattedNumber
                                  value={hourly_rate}
                                  currency={currency}
                                  style="currency"
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Text>
                            </IntlProviderHOC>
                            <div className="flex-basis-15 text-right">
                              <Button
                                iconOnly
                                onClick={openDeletePricingModal}
                                testId="calendar-rate-delete"
                                variant="ghost"
                              >
                                <TrashIcon />
                                <span className="tw-sr-only">
                                  <FormattedMessage id="hosting.calendar_rate.button_text_delete" defaultMessage="delete" />
                                </span>
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                }
              />
            </div>
          </div> : null
      }
      <div className="row margin-lg-top margin-md-bottom">
        <div className="col-xs-12">
          {
            showVariablePricingForm
              ? <VariablePricingForm handleCancel={handleCancel} />
              : (
                <Button
                  appearance="purple"
                  onClick={handleAddVariableClick}
                  testId="calendar-rate-add"
                >
                  <FormattedMessage id="hosting.calendar_rate.button_text_add_rate" defaultMessage="Add rate"/>
                </Button>
              )
          }
        </div>
      </div>
      <div className="clearfix" />
      <hr />
    </div>
  );
};

CalendarRate.propTypes = {
  calendarPrice: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  isFandB: PropTypes.bool,
  dispatch: PropTypes.func,
  showVariablePricingForm: PropTypes.bool,
  intl: intlShape
};

export default injectIntl(CalendarRate);
